import React, { useContext, useMemo } from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import { AppContext } from '../../context';
import PrivateRoute from '../../../../components/PrivateRoute';
import FeatureFlagRoute from '../../../../components/FeatureFlagRoute';
import ApplicationProfile from '../../../ApplicationProfile';
import CreateCommercialApplicant from '../../../CreateCommercialApplicant';
import AddGroup from '../../../CreateTransactionBatch/AddGroup';
import GenerateRenewalOffer from '../../../GenerateRenewalOffer';
import RentForecast from '../../../RentForecast';
import Home from '../../../Home';
import HouseholdProfile from '../../../HouseholdProfile';
import ManageBuildings from '../../../ManageBuildings';
import ManageBuildingsV2 from '../../../ManageBuildingsV2';
import ManageCommercialRentRoll from '../../../ManageCommercialRentRoll';
import ManageCommunications from '../../../ManageCommunications';
import CreateCommunication from '../../../ManageCommunications/CreateCommunication';
import ManageDelinquency from '../../../ManageDelinquency';
import ManageDelinquencyV2 from '../../../ManageDelinquency/indexV2';
import ManageFloorPlans from '../../../ManageFloorPlans/indexv2';
import ManageProperties from '../../../ManageProperties';
import ManagePropertiesV2 from '../../../ManageProperties/indexV2';
import ManageProspects from '../../../ManageProspects';
import ManageProspectsV2 from '../../../ManageProspects/V2';
import ManageRentRoll from '../../../ManageRentRoll';
import ManageRentRollV2 from '../../../ManageRentRoll/indexV2';
import ManageReport from '../../../ManageReports';
import ManageMincTransactions from '../../../ManageMincTransactions';
import ManagePicTransactions from '../../../ManagePicTransactions';
import ManageUnitAvailability from '../../../ManageUnitAvailability';
import ManageUnitAvailabilityV2 from '../../../ManageUnitAvailabilityV2';
import ManageWorkOrders from '../../../ManageWorkOrders';
import ManageWorkOrdersV2 from '../../../ManageWorkOrdersV2';
import PeopleProfile from '../../../PeopleProfile';
import ProspectProfile from '../../../ProspectProfile';
import QualificationForms from '../../../QualificationForms';
import PriorQualificationForms from '../../../QualificationForms/PriorQualificationForms';
import ResidentProfile from '../../../ResidentProfile';
import CompleteRenewalPage from '../../../ResidentProfile/CompleteRenewal';
import ViewCommunications from '../../../ViewCommunications';
import ViewProperty from '../../../ViewProperty';
import ViewUnit from '../../../ViewUnit';
import WorkOrder from '../../../WorkOrder';
import AddAmenity from '../../../Amenity/AddAmenity';
import EditAmenity from '../../../Amenity/EditAmenity';
import CreateApplicationPage from '../../../CreateApplication';
import CreateChargeBatch from '../../../CreateChargeBatch';
import CreateCommercialTenantPage from '../../../CreateCommercialTenant';
import CreateResidentPage from '../../../CreateResident';
import CreateRolePage from '../../../CreateRole';
import CreateSubsidyBatch from '../../../CreateSubsidyBatch';
import CreateTransactionBatch from '../../../CreateTransactionBatch';
import CreateUserPage from '../../../CreateUser';
import EditApplicationHousehold from '../../../EditApplicationHousehold';
import EditMasterTransactionCodes from '../../../EditMasterTransactionCode';
import EditProspectHousehold from '../../../EditProspectHousehold';
import EditResidentHousehold from '../../../EditResidentHousehold';
import EditRolePage from '../../../EditRole';
import EditUserPage from '../../../EditUser';
import FinalAccountStatement from '../../../FinalAccountStatement';
import ManageAmenities from '../../../ManageAmenities';
import ManageApplicationsPageV2 from '../../../ManageApplications/indexV2';
import ManageAffordableWaitlist from '../../../ManageAffordableWaitlist';
import ManageAffordableWaitlistV2 from '../../../ManageAffordableWaitlistV2';
import LeadManagement from '../../../LeadManagement';
import CertificationApprovals from '../../../CertificationApprovals';
import UnitCompliance from '../../../UnitCompliance';
import ManageCertificationsV2 from '../../../ManageCertifications/indexV2';
import ManageHUDVouchers from '../../../ManageHUDVouchers';
import { HUD_VOUCHER_ACCESS_SCOPES } from '../../../../hooks/useHudVoucherPermissions';
import ManageGrossRents from '../../../ManageGrossRents';
import ManageDeposits from '../../../ManageDeposits';
import ManageLeaseExpirationLimits from '../../../ManageLeaseExpirationLimits';
import ManageLeaseExpirations from '../../../ManageLeaseExpirations';
import ManageLeaseExpirationsV2 from '../../../ManageLeaseExpirations/indexV2';
import ManageMiscTransactions from '../../../ManageMiscTransactions';
import ManageOnlinePayments from '../../../ManageOnlinePayments';
import ManagePaymentBatch from '../../../ManagePaymentBatch';
import ManagePriorResidents from '../../../ManagePriorResidents';
import ManagePriorResidentsV2 from '../../../ManagePriorResidents/indexV2';
import ManagePropertyGroups from '../../../ManagePropertyGroups';
import AddEditPropertyGroup from '../../../ManagePropertyGroups/AddEditPropertyGroup';
import ManageRolesPage from '../../../ManageRoles';
import ManageSubsidyPayments from '../../../ManageSubsidyPayments';
import ManagePaymentBatches from '../../../ManagePaymentBatches';
import ManageTransactionBatches from '../../../ManageTransactionBatches/index.js';
import ManageTransactionCodes from '../../../ManageTransactionCodes';
import ManageUndepositedPayments from '../../../ManageUndepositedPayments';
import ManageUsersPage from '../../../ManageUsers';
import MonthEndClose from '../../../MonthEndClose';
import CreatePropertyDataBatch from '../../../PropertyDataBatch';
import ProrateCalculator from '../../../ProrateCalculator';
import ViewBankDeposit from '../../../ViewBankDeposit';
import AllManageLeaseExpirations from '../../../AllManageLeaseExpirations';
import AllManageReports from '../../../AllManageReports';
import AllUnitAvailability from '../../../AllUnitAvailability';
import AllWorkOrders from '../../../AllWorkOrders';
import ManageFees from '../../../ManageFees';
import CentralizedManageDocuments from '../../../CentralizedManageDocuments';
import AddDocument from '../../../AddDocument';
import EditDocument from '../../../EditDocument';
import CentralizedManageDelinquency from '../../../CentralizedManageDelinquency';
import MessagingHub from '../../../MessagingHub/MessagingHubContainer';
import ManageHudVoucherDetails from '../../../ManageHudVoucherDetails';
// Hooks
import { useFlags } from 'launchdarkly-react-client-sdk';
// eslint-disable-next-line max-len
import useAffordableWaitlistValidations from '../../../../containers/ManageAffordableWaitlist/hooks.useAffordableWaitlistValidations';

// ENG-31380 — LaunchDarkly bootstraps anonymous and re-identifies with the real user afterwards, so
// flags are falsy on the first renders and then flip. This page reads `reba-rev-mgmt` at `useForm`
// init and react-hook-form never re-applies defaultValues, so mounting early leaves the form stuck
// holding the 'MM/DD/YYYY' placeholder under a read-only field: submit disabled forever, and that
// branch has no helperText to say why. Same shape as withApplicationFormRedirects in Routes/index.js.
function GenerateRenewalOfferWhenLdReady(props) {
  const { isLdUserContextReady } = useContext(AppContext);

  if (!isLdUserContextReady) return null;

  return <GenerateRenewalOffer {...props} />;
}

function PageContent() {
  const {
    selectedProperty,
    permissions,
    userOrganizationId: organizationId,
  } = useContext(AppContext);

  /*
  const { pathname, search } = history.location;
  const [currentLocation, setCurrentLocation] = useState(null);
  const [prevLocation, setPrevLocation] = useState(null);

  useEffect(() => {
    setPrevLocation(currentLocation);
    setCurrentLocation(pathname + search);
  }, [pathname, search]);
  */

  // `null` until the property arrives, so a route gated on one of these waits instead of reading an
  // unloaded property as "program absent" and redirecting to /404.
  const { propertyHasRD, propertyHasHUD, propertyHasPH } = useMemo(() => {
    if (!selectedProperty) {
      return {
        propertyHasRD: null,
        propertyHasHUD: null,
        propertyHasPH: null,
      };
    }
    const hasProgram = (name) =>
      selectedProperty.pap?.some(
        (item) => item.masterAffordableProgram?.name === name,
      ) ?? false;
    return {
      propertyHasRD: hasProgram('RD'),
      propertyHasHUD: hasProgram('HUD'),
      propertyHasPH: hasProgram('PH'),
    };
  }, [selectedProperty]);

  const { canAccessManageAffordableWaitlist } =
    useAffordableWaitlistValidations();

  const {
    manageRentRollNewTable: enableManageRentRollNewTable = false,
    manageDelinquentAndPrepaidTableConversion = false,
    moveActionColumnsToLeft = false,
    manageProspectsNewTable = false,
    managePriorResidentsTableConversion = false,
    manageWorkOrdersTableConversion: isManageWorkOrdersV2On,
    propertyMuaUpdate: propertyMuaUpdateFlag,
    affordableWaitlistV2,
    managePropertiesTableUpdate: enableManagePropertiesV2 = false,
    manageBuildings = false,
  } = useFlags();
  return (
    <Switch>
      <PrivateRoute exact path="/" component={Home} />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId"
        component={Home}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read']}
        exact
        path="/property/:propertyId/management"
        component={
          manageProspectsNewTable ? ManageProspectsV2 : ManageProspects
        }
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-create']}
        exact
        path="/property/:propertyId/prospect"
        component={ProspectProfile}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-update', 'prospect-assign']}
        exact
        path="/property/:propertyId/prospect/:prospectId"
        component={PeopleProfile}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-update']}
        exact
        path="/property/:propertyId/prospect/:prospectId/household"
        component={EditProspectHousehold}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-update', 'prospect-assign']}
        exact
        path="/property/:propertyId/application/:applicationId"
        component={ApplicationProfile}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['resident-read', 'resident-edit', 'resident-create']}
        exact
        path="/property/:propertyId/resident/:residentId"
        component={ResidentProfile}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['resident-read', 'resident-edit', 'resident-create']}
        exact
        path="/property/:propertyId/resident/:residentId/household"
        component={EditResidentHousehold}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['resident-read', 'resident-edit', 'resident-create']}
        exact
        path="/property/:propertyId/prior-resident/:residentId"
        component={ResidentProfile}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['final-account-statement-create']}
        exact
        path="/property/:propertyId/prior-resident/:residentId/final-account-statement"
        component={FinalAccountStatement}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['resident-read', 'resident-edit', 'resident-create']}
        exact
        path="/property/:propertyId/manage-prior-residents"
        component={
          managePriorResidentsTableConversion
            ? ManagePriorResidentsV2
            : ManagePriorResidents
        }
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-update', 'prospect-assign']}
        exact
        path="/property/:propertyId/application/:applicationId/household"
        component={EditApplicationHousehold}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['user-read']}
        exact
        path="/manage-users"
        component={ManageUsersPage}
      />
      <FeatureFlagRoute
        flagName="manageAmenitiesV1"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['amenities-view-add-edit']}
        exact
        path="/property/:propertyId/manage-amenities"
        component={ManageAmenities}
      />
      <FeatureFlagRoute
        flagName="manageAmenitiesV1"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['amenities-view-add-edit']}
        exact
        path="/property/:propertyId/amenity"
        component={AddAmenity}
      />
      <FeatureFlagRoute
        flagName="manageAmenitiesV1"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['amenities-view-add-edit']}
        exact
        path="/property/:propertyId/amenity/:propertyFeeId"
        component={EditAmenity}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['user-create']}
        exact
        path="/add-user"
        component={CreateUserPage}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['user-read', 'user-update', 'user-delete']}
        exact
        path="/edit-user/:userId"
        component={EditUserPage}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['workorder-create']}
        exact
        path="/property/:propertyId/add-work-order"
        component={WorkOrder}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['role-create']}
        exact
        path="/add-role"
        component={CreateRolePage}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['role-read', 'role-update', 'role-delete']}
        exact
        path="/edit-role/:roleId"
        component={EditRolePage}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['role-read']}
        exact
        path="/manage-roles"
        component={ManageRolesPage}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read']}
        exact
        path="/property/:propertyId/manage-applications"
        component={ManageApplicationsPageV2}
      />
      <FeatureFlagRoute
        flagName="affordableWaitlist"
        additionalCondition={
          selectedProperty ? canAccessManageAffordableWaitlist : null
        }
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read']}
        exact
        path="/property/:propertyId/manage-affordable-waitlist"
        component={
          affordableWaitlistV2
            ? ManageAffordableWaitlistV2
            : ManageAffordableWaitlist
        }
      />
      <FeatureFlagRoute
        flagName="leadManagementFunnel"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read']}
        exact
        path="/property/:propertyId/lead-management"
        component={LeadManagement}
      />
      <FeatureFlagRoute
        flagName="certApprovalFunnel"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-update']}
        exact
        path="/property/:propertyId/certification-approvals"
        component={CertificationApprovals}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-create']}
        exact
        path="/property/:propertyId/create-application/:prospectId"
        component={CreateApplicationPage}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-create']}
        exact
        path="/property/:propertyId/create-resident/:applicationId"
        component={CreateResidentPage}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-create']}
        exact
        path="/property/:propertyId/create-commercial-tenant/:applicationId"
        component={CreateCommercialTenantPage}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-create']}
        exact
        path="/property/:propertyId/complete-lease-renewal/:residentId/:leaseId"
        component={CompleteRenewalPage}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-delinquent-and-prepaid-read']}
        exact
        path="/property/:propertyId/manage-delinquent-and-prepaid"
        component={
          manageDelinquentAndPrepaidTableConversion
            ? ManageDelinquencyV2
            : ManageDelinquency
        }
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['workorder-read']}
        exact
        path="/property/:propertyId/manage-work-orders"
        component={
          isManageWorkOrdersV2On ? ManageWorkOrdersV2 : ManageWorkOrders
        }
      />
      <FeatureFlagRoute
        flagName="centralizedWorkOrders"
        userPermissions={permissions}
        organizationId={organizationId}
        scope={['workorder-read']}
        exact
        path="/work-orders"
        component={AllWorkOrders}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['workorder-read']}
        exact
        path="/property/:propertyId/edit-work-order/:workOrderId"
        component={WorkOrder}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['deposits-read']}
        exact
        path="/property/:propertyId/manage-deposits"
        component={ManageDeposits}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['deposits-read']}
        exact
        path="/property/:propertyId/deposit/:depositId"
        component={ViewBankDeposit}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['misc-income-read', 'misc-income-create', 'misc-income-edit']}
        exact
        path="/property/:propertyId/manage-misc-income"
        component={ManageMiscTransactions}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['month-end-close']}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/month-end-close"
        component={MonthEndClose}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['charge-batch-create']}
        exact
        path="/property/:propertyId/create-charge-batch"
        component={CreateChargeBatch}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['charge-batch-create']}
        exact
        path="/property/:propertyId/property-data-batch"
        component={CreatePropertyDataBatch}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['payment-batch-manage']}
        exact
        path="/property/:propertyId/payment-batch/:batchId?"
        component={ManagePaymentBatch}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['payment-batch-manage']}
        exact
        path="/property/:propertyId/manage-undeposited-payments"
        component={ManageUndepositedPayments}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['forecast-rents-read']}
        exact
        path="/property/:propertyId/forecast-rents"
        component={RentForecast}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['property-read', 'property-update']}
        exact
        path="/properties"
        component={
          enableManagePropertiesV2 ? ManagePropertiesV2 : ManageProperties
        }
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-units-read']}
        exact
        path="/property/:propertyId/manage-rent-roll"
        component={
          enableManageRentRollNewTable ? ManageRentRollV2 : ManageRentRoll
        }
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-units-read']}
        exact
        path="/property/:propertyId/manage-commercial-rent-roll"
        component={ManageCommercialRentRoll}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-units-read']}
        exact
        path="/property/:propertyId/manage-unit-availability"
        component={
          propertyMuaUpdateFlag
            ? ManageUnitAvailabilityV2
            : ManageUnitAvailability
        }
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-units-read']}
        exact
        path="/property/:propertyId/manage-lease-expirations"
        component={
          moveActionColumnsToLeft
            ? ManageLeaseExpirationsV2
            : ManageLeaseExpirations
        }
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-units-read']}
        exact
        path="/property/:propertyId/manage-certifications"
        component={ManageCertificationsV2}
      />
      <FeatureFlagRoute
        flagName="naur"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/unit-compliance"
        component={UnitCompliance}
      />
      <FeatureFlagRoute
        flagName="hudManageVouchers"
        additionalCondition={propertyHasHUD}
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={HUD_VOUCHER_ACCESS_SCOPES}
        exact
        path="/property/:propertyId/manage-hud-vouchers"
        component={ManageHUDVouchers}
      />
      <FeatureFlagRoute
        flagName="updatedHudGrcLogic"
        additionalCondition={propertyHasHUD}
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['view-manage-gross-rents']}
        exact
        path="/property/:propertyId/manage-gross-rents"
        component={ManageGrossRents}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-units-read']}
        exact
        path="/property/:propertyId/unit/:unitId"
        component={ViewUnit}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['floor-plan-read']}
        exact
        path="/property/:propertyId/floorplan/:floorplanId"
        component={ViewUnit}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-reports-read']}
        exact
        path="/property/:propertyId/manage-reports"
        component={ManageReport}
      />
      <FeatureFlagRoute
        flagName="mincTransactionScreen"
        additionalCondition={propertyHasRD}
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-reports-read']}
        exact
        path="/property/:propertyId/manage-minc-transactions"
        component={ManageMincTransactions}
      />
      {propertyHasPH && (
        <PrivateRoute
          userPermissions={permissions}
          selectedProperty={selectedProperty}
          scope={['manage-reports-read']}
          exact
          path="/property/:propertyId/manage-pic-transactions"
          component={ManagePicTransactions}
        />
      )}
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/communications"
        component={ViewCommunications}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['lease-create', 'lease-read']}
        exact
        path="/property/:propertyId/generate-renewal-offer/:residentId"
        component={GenerateRenewalOfferWhenLdReady}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-update', 'prospect-assign']}
        exact
        // eslint-disable-next-line
        path="/property/:propertyId/affordable-qualifications/:affordableQualificationId/affordable-programs/:programName/income-verification/:applicantId"
        component={QualificationForms}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-update', 'prospect-assign']}
        exact
        // eslint-disable-next-line
        path="/property/:propertyId/affordable-qualifications/:affordableQualificationId/affordable-programs/:programName/asset-information/:applicantId"
        component={QualificationForms}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-update', 'prospect-assign']}
        exact
        // eslint-disable-next-line max-len
        path="/property/:propertyId/prior-income/:applicationId/:qualificationHistoryId/:checklistId/:residentId"
        component={PriorQualificationForms}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-read', 'prospect-update', 'prospect-assign']}
        exact
        // eslint-disable-next-line max-len
        path="/property/:propertyId/prior-assets/:applicationId/:qualificationHistoryId/:checklistId/:residentId"
        component={PriorQualificationForms}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['floor-plan-read']}
        exact
        path="/property/:propertyId/manage-floor-plan"
        component={ManageFloorPlans}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['manage-fees-view']}
        exact
        path="/property/:propertyId/manage-fees"
        component={ManageFees}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/manage-communications"
        component={ManageCommunications}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['communication-create']}
        exact
        path="/property/:propertyId/create-communication"
        component={CreateCommunication}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['communication-create']}
        exact
        path="/property/:propertyId/view-communication"
        component={CreateCommunication}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['transaction-codes-edit', 'transaction-codes-read']}
        exact
        path="/manage-transaction-codes"
        component={ManageTransactionCodes}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['transaction-codes-edit', 'transaction-codes-read']}
        exact
        path="/edit-transaction-codes/:masterTransactionCodeId"
        component={EditMasterTransactionCodes}
      />
      <PrivateRoute
        userPermissions={permissions}
        scope={['property-read', 'property-update']}
        exact
        path="/properties/:propertyId"
        component={ViewProperty}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/manage-buildings"
        component={manageBuildings ? ManageBuildingsV2 : ManageBuildings}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['prospect-create']}
        exact
        path="/property/:propertyId/commercial-applicant"
        component={CreateCommercialApplicant}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/manage-subsidy-payments"
        component={ManageSubsidyPayments}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['payment-batch-manage']}
        exact
        path="/property/:propertyId/manage-payment-batches"
        component={ManagePaymentBatches}
      />
      <PrivateRoute
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/create-subsidy-batch/:paymentBatchHeaderId?"
        component={CreateSubsidyBatch}
      />
      <FeatureFlagRoute
        flagName="leaseExpirationMgmtV1"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        scope={['lease-expirations-limit-view']}
        exact
        path="/:organizationId/manage-lease-expiration-limits/:propertyId"
        component={ManageLeaseExpirationLimits}
      />
      <FeatureFlagRoute
        flagName="transactionBatchV1"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/manage-transaction-batches"
        component={ManageTransactionBatches}
      />
      <FeatureFlagRoute
        flagName="transactionBatchV1"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/create-transaction-batch/:transactionBatchHeaderId?"
        component={CreateTransactionBatch}
      />
      <FeatureFlagRoute
        flagName="transactionBatchV2Addgroup"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/add-group/:transactionBatchHeaderId?"
        component={AddGroup}
      />
      <FeatureFlagRoute
        flagName="prorateCalculatorV1"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/prorate-calculator"
        component={ProrateCalculator}
      />
      <FeatureFlagRoute
        flagName="paymentsV1"
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/manage-online-payments"
        component={ManageOnlinePayments}
      />
      <FeatureFlagRoute
        flagName="customerOverhaul"
        userPermissions={permissions}
        organizationId={organizationId}
        exact
        path="/property/:propertyId/household/:householdId"
        component={HouseholdProfile}
      />
      <FeatureFlagRoute
        flagName="adminEnhancementsPropertiesManagegroups"
        userPermissions={permissions}
        organizationId={organizationId}
        exact
        path="/manage-property-groups"
        component={ManagePropertyGroups}
      />
      <FeatureFlagRoute
        flagName="adminEnhancementsPropertiesManagegroups"
        userPermissions={permissions}
        organizationId={organizationId}
        exact
        path="/create-property-group"
        component={AddEditPropertyGroup}
      />
      <FeatureFlagRoute
        flagName="adminEnhancementsPropertiesManagegroups"
        userPermissions={permissions}
        organizationId={organizationId}
        exact
        path="/property-group/:propertyGroupId"
        component={AddEditPropertyGroup}
      />
      <FeatureFlagRoute
        flagName="centralizedUnitAvailability"
        userPermissions={permissions}
        organizationId={organizationId}
        scope={['manage-units-read']}
        exact
        path="/manage-unit-availability"
        component={AllUnitAvailability}
      />
      <FeatureFlagRoute
        flagName="centralizedExpiringLeases"
        userPermissions={permissions}
        organizationId={organizationId}
        scope={['manage-units-read']}
        exact
        path="/lease-expirations"
        component={AllManageLeaseExpirations}
      />
      <FeatureFlagRoute
        flagName="centralizedDelinquencyAndPrepaid"
        userPermissions={permissions}
        scope={['manage-delinquent-and-prepaid-read']}
        exact
        path="/manage-delinquent-and-prepaid"
        component={CentralizedManageDelinquency}
      />
      <PrivateRoute
        userPermissions={permissions}
        organizationId={organizationId}
        scope={['manage-reports-read']}
        exact
        path="/manage-reports"
        component={AllManageReports}
      />
      <FeatureFlagRoute
        flagName="documentManagementMvp"
        userPermissions={permissions}
        organizationId={organizationId}
        scope={['centralized-docs-read']}
        exact
        path="/manage-documents"
        component={CentralizedManageDocuments}
      />
      <FeatureFlagRoute
        flagName="documentManagementMvp"
        userPermissions={permissions}
        organizationId={organizationId}
        scope={['centralized-docs-read', 'centralized-docs-edit']}
        exact
        path="/add-document"
        component={AddDocument}
      />
      <FeatureFlagRoute
        flagName="documentManagementMvp"
        userPermissions={permissions}
        organizationId={organizationId}
        scope={['centralized-docs-read']}
        exact
        path="/documents/:documentId"
        component={EditDocument}
      />
      <FeatureFlagRoute
        flagName="messagingHub"
        userPermissions={permissions}
        organizationId={organizationId}
        exact
        path="/message-hub"
        component={MessagingHub}
        scope={['communication-create']}
      />
      <FeatureFlagRoute
        flagName="messagingHub"
        additionalCondition={
          selectedProperty
            ? selectedProperty.isTwoWayCommunicationActive === true
            : null
        }
        userPermissions={permissions}
        selectedProperty={selectedProperty}
        exact
        path="/property/:propertyId/message-hub"
        component={MessagingHub}
        scope={['communication-create']}
      />
      <FeatureFlagRoute
        flagName="hudManageVouchers"
        additionalCondition={propertyHasHUD}
        userPermissions={permissions}
        organizationId={organizationId}
        exact
        path="/property/:propertyId/manage-hud-vouchers/voucher-details/:voucherId"
        component={ManageHudVoucherDetails}
        scope={HUD_VOUCHER_ACCESS_SCOPES}
        selectedProperty={selectedProperty}
      />
      <Route>
        <Redirect to="/404" />
      </Route>
    </Switch>
  );
}

export default PageContent;
